import type { SvgIconProps } from '@mui/material';
import type { ComponentType } from 'react';
/* eslint-disable boundaries/element-types -- dataConfigValidators are consumed here as pure validation schemas,
   not as widget UI; this feature acts as a cross-cutting registry that must reference all view-type validators by design. */
import { dataConfigValidator as calendarInsightsDataConfigValidator } from '@/widgets/calendarInsights';
import { dataConfigValidator as cmpDataConfigValidator } from '@/widgets/cohortMarketingPerformance';
import { dataConfigValidator as cohortRetentionDataConfigValidator } from '@/widgets/cohortRetention';
import { dataConfigValidator as cohortScannerDataConfigValidator } from '@/widgets/cohortScanner';
import { dataConfigValidator as roiBreakdownDataConfigValidator } from '@/widgets/roiBreakdown';
import {
  calendarChartDataConfigValidator,
  cohortChartDataConfigValidator,
} from '@/features/chartsView';
/* eslint-enable boundaries/element-types */
import { ROUTES } from '@/shared/constants';
import {
  CalendarChartIcon,
  CalendarInsightsIcon,
  CohortChartIcon,
  CohortMarketingPerformanceIcon,
  CohortRetentionIcon,
  CohortScannerIcon,
  ROIBreakdownIcon,
} from '@/shared/ui';
import { PresetViewType } from '../api/types';
import type { Validator } from '../model/types';

export type ViewTypeConfig = {
  label: string;
  Icon: ComponentType<SvgIconProps>;
  route: string;
  validator: Validator;
};

export const VIEW_TYPE_CONFIG: Record<PresetViewType, ViewTypeConfig> = {
  [PresetViewType.cohortMarketingPerformance]: {
    label: 'Cohort Marketing Performance',
    Icon: CohortMarketingPerformanceIcon,
    route: ROUTES.cohortMarketingPerformance,
    validator: (v) => cmpDataConfigValidator.safeParse(v),
  },
  [PresetViewType.calendarInsights]: {
    label: 'Calendar Insights',
    Icon: CalendarInsightsIcon,
    route: ROUTES.calendarInsights,
    validator: (v) => calendarInsightsDataConfigValidator.safeParse(v),
  },
  [PresetViewType.cohortChart]: {
    label: 'Cohort Chart',
    Icon: CohortChartIcon,
    route: ROUTES.cohortChart,
    validator: (v) => cohortChartDataConfigValidator.safeParse(v),
  },
  [PresetViewType.calendarChart]: {
    label: 'Calendar Chart',
    Icon: CalendarChartIcon,
    route: ROUTES.calendarChart,
    validator: (v) => calendarChartDataConfigValidator.safeParse(v),
  },
  [PresetViewType.cohortRetention]: {
    label: 'Cohort Retention',
    Icon: CohortRetentionIcon,
    route: ROUTES.cohortRetention,
    validator: (v) => cohortRetentionDataConfigValidator.safeParse(v),
  },
  [PresetViewType.cohortScanner]: {
    label: 'Cohort Scanner',
    Icon: CohortScannerIcon,
    route: ROUTES.cohortScanner,
    validator: (v) => cohortScannerDataConfigValidator.safeParse(v),
  },
  [PresetViewType.roiBreakdown]: {
    label: 'ROI Breakdown',
    Icon: ROIBreakdownIcon,
    route: ROUTES.roiBreakdown,
    validator: (v) => roiBreakdownDataConfigValidator.safeParse(v),
  },
};

export const VIEW_VALIDATORS = Object.fromEntries(
  Object.entries(VIEW_TYPE_CONFIG).map(([k, v]) => [k, v.validator]),
) as Record<PresetViewType, Validator>;
